/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import createEntityObserver from './createEntityObserver';
import {EntitySubscription} from './types';

const jobProgress = createEntityObserver(EntitySubscription.jobProgress);

/**
 * The JobProgressObserver API allows for receiving the progress of a Job, or
 * collection of Jobs, as its tasks advance, whereby a change is received for
 * every task status and device count change. This API essentially delegates to
 * the EntityObserver API, only in the context of Job progress only.
 *
 * Note, while initialization of the EntityObserver is generally performed during
 * application initialization, as a best practice, always add observers via the
 * EntityObserver.onInitialized method, which will invoke the provided callback
 * once initialized, or immediately if already initialized:
 *
 * ```typescript
 *
 *   import EntityObserver from '@ion/services/src/websocket/EntityObserver';
 *   import JobProgressObserver from '@ion/services/src/websocket/JobProgressObserver';
 *
 *   EntityObserver.onInitialized(() => {
 *     JobProgressObserver.addObserver(job.id, onJobProgress);
 *   });
 *
 * ```
 * Once a Job (or collection of Jobs) no longer need to be observed, make sure
 * to invoke removeObserver ...
 *
 * ```typescript
 *
 *   JobProgressObserver.removeObserver(job.id, onJobProgress);
 *
 * ```
 *
 * Note, a received change is merged by the consumer which owns the cached Job,
 * not by this API.
 *
 * @see JobStatusObserver
 */
export default class JobProgressObserver {
  /**
   * The addObserver method allows for observing a Job or collection of Jobs
   * for the progress of their tasks.
   *
   * ```typescript
   *
   *   import EntityObserver from '@ion/services/src/websocket/EntityObserver';
   *   import JobProgressObserver from '@ion/services/src/websocket/JobProgressObserver';
   *
   *   EntityObserver.onInitialized(() => {
   *     JobProgressObserver.addObserver(job.id, onJobProgress);
   *   });
   *
   * ```
   * @param   ids Either a single id, or array of job ids to be observed for
   *          changes.
   * @param   observer Callback function which will be invoked upon receiving
   *          changes for the observed jobs.
   * @returns true if the observer has been added, otherwise, false. Generally,
   *          an observer will only ever fail to be added if the connection
   *          has been closed or has not yet been established.
   */
  static addObserver = jobProgress.addObserver;

  /**
   * The removeObserver method allows for removing a Job or collection of Jobs
   * from being observed for progress. This method must always be invoked when
   * an observer is no longer needed.
   *
   * ```typescript
   *
   *   import JobProgressObserver from '@ion/services/src/websocket/JobProgressObserver';
   *
   *   JobProgressObserver.removeObserver(job.id, onJobProgress);
   *
   * ```
   *
   * @param   ids Either a single id, or array of job ids to no longer be
   *          observed for changes.
   * @param   observer The callback function previously passed to `addObserver`
   *          for the given ids.
   * @returns true if the observer has been removed, otherwise, false. Generally,
   *          an observer will only ever fail to be removed if the connection
   *          has been closed or has not yet been established.
   */
  static removeObserver = jobProgress.removeObserver;

  /**
   * Provides a utility which allows for removing all job progress observers
   * within an application, leaving the observers of every other entity type,
   * including those of the job status channel, intact.
   *
   * ```typescript
   *
   *   import JobProgressObserver from '@ion/services/src/websocket/JobProgressObserver';
   *
   *   // unsubscribe and remove all jobs from being observed for progress ...
   *   JobProgressObserver.removeAllObservers();
   *
   * ```
   *
   * @returns true if the observers have been removed, otherwise, false.
   *          Generally, an observer will only ever fail to be removed if the
   *          connection has been closed or has not yet been established.
   */
  static removeAllObservers = jobProgress.removeAllObservers;
}
